import { GetStaticPaths, NextPage } from 'next'
import { Fragment, useMemo } from 'react'

import { useRouter } from 'next/router'

import { useTranslation } from 'next-i18next'

import { getVccData } from '~/../utils/data-fetch/bootcamp/bootcamp.query'

import { AppLanguage } from '~/types/locale'

import Metatags from '~/components/Metadata/Metatags'
import { Video, SanityVideo, MetaImage } from '~/components/Metadata/Metadata.types'

import VideoSchema from '~/components/Metadata/Schema/VideoSchema'

import {
  BootcampPageUrlParamsType,
  CategoryPathUrls,
  LevelActionType,
  LevelPageDetailsType,
} from '~/bootcamp/types/bootcamp.types'
import { selectBootcampPageSectionName, setBootcampPageData } from '~/bootcamp/slices/bootcamp-page.slice'
import {
  selectVccPageTitle,
  selectVccPageLevelsData,
  setVideoTopicColor,
  setVccData,
} from '~/bootcamp/slices/vcc-data.slice'
import { CATEGORY_COLORS } from '~/bootcamp/constants/bootcamp.const'

import { getFileUrlWithoutEncoding, getImageUrlWithoutEncoding } from '~/utils/cdnHelpers'
import { GetProps } from '~/utils/pageWrapper/types'
import { getStaticPropsWrapper } from '~/utils/pageWrapper/staticPageWrapper'
import { DefaultLayout } from '~/shared/layouts/DefaultLayout'

import { getFormattedVideoUrls, getVideoTypeFromUrl } from '~/utils'
import { DEFAULT_LAYOUT_TRANSLATION_MODULE_NAMES } from '~/shared/layouts/constants'
import VideoCourse from '~/bootcamp/pages/BootcampView/VideoCourse'
import { useAppSelector } from '~/redux/hooks'
import { processVccPageData } from '~/bootcamp/utils/getVccPageDetail.utils'
import { setCurrActivityTypeActionCreator } from '~/bootcamp/slices/bootcamp-activity.slice'
import { isActivityNotFound, isVideoTopicNotFound } from '~/bootcamp/pages/BootcampView/BootcampView.utils'

import { usePageTitle } from '~/utils/tags'
import { SourceType } from '~/components/VideoPlayer/VideoPlayer.types'
import useRedirectV2UserToGradeOverview from '~/curriculum/hooks/useRedirectV2UserToGradeOverview'

function getVideoUrl(awsVideo: SanityVideo): SourceType {
  const sources: SourceType[] =
    awsVideo && awsVideo.videoUrls && awsVideo.videoUrls.length
      ? getFormattedVideoUrls(awsVideo.videoUrls)
      : [{ src: getFileUrlWithoutEncoding(awsVideo.srcKey), type: getVideoTypeFromUrl(awsVideo.srcKey) || 'video/mp4' }]
  return sources[0]
}

function useRichData(data: LevelPageDetailsType): { video?: Video; image?: MetaImage } {
  const awsVideo = data.content?.find((item: { _type: string }) => item._type === 'awsVideo') as undefined | SanityVideo
  const { t } = useTranslation('bootcamp')

  let video = undefined
  let image = undefined
  if (awsVideo) {
    const videoUrl = getVideoUrl(awsVideo)
    video = {
      url: videoUrl.src,
      urlType: videoUrl.type,
      thumbnailUrl: getImageUrlWithoutEncoding(awsVideo.thumbnailUrl),
      uploadDate: awsVideo.uploadDate,
      width: 1280,
      height: 720,
      alt: awsVideo.description,
      duration: awsVideo.duration ? `PT${awsVideo.duration}S` : undefined, // ISO 8601 duration format
      genre: t('videoGenre') as string,
    }
    image = {
      url: video.thumbnailUrl,
    }
  }

  return {
    video,
    image,
  }
}

function isVideoUrlContainEn(url: string): boolean {
  return url.includes('en/') || url.includes('en%2F') || url.includes('storage.googleapis.com')
}

const VideoCourseNextPage: NextPage<{ pageData: LevelPageDetailsType }> = ({ pageData }) => {
  // When opened in grade context (?showGrade=true), redirect Curriculum v2 users to their v2 grade overview.
  useRedirectV2UserToGradeOverview()

  const { query, locale } = useRouter()
  const { action, level } = query as unknown as BootcampPageUrlParamsType

  const vccTopicTitle = useAppSelector(selectVccPageTitle) || ''
  const sectionName = useAppSelector(selectBootcampPageSectionName) || ''

  const title = usePageTitle(useGetPageTitleByType(vccTopicTitle, sectionName, action, level))
  const { video, image } = useRichData(pageData)
  const isEnglishOnly = useMemo(() => {
    if (locale !== AppLanguage.EN && !(action === LevelActionType.Activity || action === LevelActionType.Quiz)) {
      return !!(video && isVideoUrlContainEn(video.url))
    }
    return false
  }, [action, locale, video])

  return (
    <Fragment>
      <Metatags
        contentType={video ? 'video.other' : 'website'}
        image={image}
        metaDescription={pageData.metaDescription}
        noindex={pageData.noindex}
        title={title}
        video={video}
      />
      {video && <VideoSchema details={pageData} video={video} />}
      <DefaultLayout topbarClassName="not-mobile" zeroPadding>
        <VideoCourse isEnglishOnly={isEnglishOnly} />
      </DefaultLayout>
    </Fragment>
  )
}

/**
 * Finds Bootcamp Page Title By Page Type
 *
 * @param title Bootcamp Name e.g. What is Multiplication?
 * @param sectionName Bootcamp Page Section Name e.g. Why Should You Learn Multiplication?
 * @param pageType Bootcamp Page Type e.g. Why, How, Definition etc.
 * @param level Current level number
 * @returns Page Title
 */
const useGetPageTitleByType = (
  title: string,
  sectionName: string,
  pageType: LevelActionType,
  level?: string,
): string => {
  const { t } = useTranslation('bootcampQuiz')
  const levelsPageData = useAppSelector(selectVccPageLevelsData)

  switch (pageType) {
    case LevelActionType.Quiz:
      return t('quizPage')
    default: {
      // Find the shortTitle for the current level
      const currentLevelData = levelsPageData?.find((levelData: any) => levelData.level === level)
      return currentLevelData?.shortTitle || sectionName
    }
  }
}

export const getStaticPaths: GetStaticPaths = () => {
  return {
    paths: [],
    fallback: 'blocking',
  }
}

export const getProps: GetProps =
  (store) =>
  async ({ locale, params }) => {
    const subtopic = params?.subtopic as CategoryPathUrls
    if (
      isVideoTopicNotFound(subtopic) ||
      !Object.values(LevelActionType).includes(params?.action as LevelActionType) ||
      (params?.type && params?.action !== LevelActionType.Activity)
    ) {
      return {
        notFound: true,
      }
    }
    let noindex = false
    let vccDetails = await getVccData({
      key: params ? (params.key as string) : '',
      module: params ? (params.moduleKey as string) : '',
      level: params ? (params.level as string) : '',
      type: params ? (params.action as LevelActionType) : LevelActionType.How,
      language: locale as AppLanguage,
    })
    if (locale !== AppLanguage.EN && !vccDetails.module) {
      vccDetails = await getVccData({
        key: params ? (params.key as string) : '',
        module: params ? (params.moduleKey as string) : '',
        level: params ? (params.level as string) : '',
        type: params ? (params.action as LevelActionType) : LevelActionType.How,
        language: AppLanguage.EN,
      })
      noindex = true
    }

    const pageData = processVccPageData(vccDetails?.module, params as unknown as BootcampPageUrlParamsType)
    if (params && params.action === LevelActionType.Activity) {
      const activityType = Number(params.type as string)
      if (isActivityNotFound(activityType, pageData?.activities?.length || 0)) {
        return { notFound: true }
      }
      store.dispatch(setCurrActivityTypeActionCreator(activityType))
    }
    if (!vccDetails.module || !pageData.valid) {
      return {
        notFound: true,
      }
    } else {
      store.dispatch(setVccData(vccDetails)) // Save VCC Sanity Data to redux store

      store.dispatch(setBootcampPageData(pageData)) // Save Bootcamp Page Data to redux store

      store.dispatch(setVideoTopicColor(CATEGORY_COLORS[subtopic])) // Set Vcc Topic Color to redux store
    }

    return {
      props: {
        path: `/bootcamp/${subtopic}/${params?.key}/${params?.moduleKey}/${params?.level}/${params?.action}`,
        namespaces: [
          ...DEFAULT_LAYOUT_TRANSLATION_MODULE_NAMES,
          'avatar',
          'bootcamp',
          'bootcampQuiz',
          'bootcampActivity',
          'panels',
          'leaderboard',
          'encyclopedia',
        ],
        pageData: { ...pageData, noindex },
      },
      revalidate:
        process.env.NEXT_PUBLIC_INSTANT_SANITY_PREVIEW === 'true'
          ? 1
          : process.env.NEXT_PUBLIC_ENVIRONMENT === 'staging'
          ? 60
          : undefined,
    }
  }

export const getStaticProps = getStaticPropsWrapper(getProps)

export default VideoCourseNextPage
